import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Alert,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BlockIcon from "@mui/icons-material/Block";
import RestoreIcon from "@mui/icons-material/Restore";
import {
  getPcps,
  createPcpConfig,
  updatePcpConfig,
  deactivatePcpConfig,
  reactivatePcpConfig,
} from "../../../../utils/services/control/masterDataService";
import ConfirmDialog from "../common/ConfirmDialog";

const PCP_TITLES = ["MD", "DO", "APRN", "PA", "NP"];

const PcpConfigPanel = ({ employee, locations = [] }) => {
  const [pcpConfigs, setPcpConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingPcp, setEditingPcp] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    locationId: "",
    title: "",
    highRisk: true,
    highCost: true,
    medicare: true,
    critical: false,
    ageMin: "",
    ageMax: "",
    maxPatient: "",
    daysWork: "",
  });

  const loadPcps = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPcps({ employeePcpId: employee.employeeId });
      setPcpConfigs(data);
    } catch (err) {
      console.error("Error loading PCP configs:", err);
    } finally {
      setLoading(false);
    }
  }, [employee.employeeId]);

  useEffect(() => {
    loadPcps();
  }, [loadPcps]);

  const handleOpenCreate = () => {
    setEditingPcp(null);
    setForm({
      locationId: "",
      title: "",
      highRisk: true,
      highCost: true,
      medicare: true,
      critical: false,
      ageMin: "",
      ageMax: "",
      maxPatient: "",
      daysWork: "",
    });
    setError("");
    setFormOpen(true);
  };

  const handleOpenEdit = (pcp) => {
    setEditingPcp(pcp);
    setForm({
      locationId: pcp.locationId,
      title: pcp.title || "",
      highRisk: pcp.highRisk,
      highCost: pcp.highCost,
      medicare: pcp.medicare,
      critical: pcp.critical,
      ageMin: pcp.ageMin ?? "",
      ageMax: pcp.ageMax ?? "",
      maxPatient: pcp.maxPatient ?? "",
      daysWork: pcp.daysWork ?? "",
    });
    setError("");
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!editingPcp && !form.locationId)
      return setError("Location is required");

    setSaving(true);
    try {
      const data = {
        employeePcpId: employee.employeeId,
        locationId: Number(form.locationId),
        title: form.title || null,
        highRisk: form.highRisk,
        highCost: form.highCost,
        medicare: form.medicare,
        critical: form.critical,
        ageMin: form.ageMin !== "" ? Number(form.ageMin) : null,
        ageMax: form.ageMax !== "" ? Number(form.ageMax) : null,
        maxPatient: form.maxPatient !== "" ? Number(form.maxPatient) : null,
        daysWork: form.daysWork !== "" ? Number(form.daysWork) : null,
      };

      if (editingPcp) {
        await updatePcpConfig(data);
      } else {
        await createPcpConfig(data);
      }
      setFormOpen(false);
      loadPcps();
    } catch (err) {
      setError(err.response?.data?.message || "Error saving PCP config");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    const pcp = confirmAction?.pcp;
    setSaving(true);
    try {
      await deactivatePcpConfig({
        employeePcpId: employee.employeeId,
        locationId: pcp.locationId,
      });
      setConfirmAction(null);
      loadPcps();
    } catch (err) {
      console.error("Error deactivating PCP:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleReactivate = async () => {
    const pcp = confirmAction?.pcp;
    setSaving(true);
    try {
      await reactivatePcpConfig({
        employeePcpId: employee.employeeId,
        locationId: pcp.locationId,
      });
      setConfirmAction(null);
      loadPcps();
    } catch (err) {
      console.error("Error reactivating PCP:", err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <CircularProgress size={24} />;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          PCP Configurations
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          Add PCP Config
        </Button>
      </Box>

      {pcpConfigs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No PCP configurations found.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Location</TableCell>
                <TableCell>Title</TableCell>
                <TableCell align="center">Patients</TableCell>
                <TableCell align="center">Capacity</TableCell>
                <TableCell>Flags</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pcpConfigs.map((pcp) => (
                <TableRow key={`${pcp.employeePcpId}-${pcp.locationId}`}>
                  <TableCell>{pcp.medicalCenter}</TableCell>
                  <TableCell>{pcp.title || "—"}</TableCell>
                  <TableCell align="center">
                    {pcp.patientsAssigned ?? 0}
                  </TableCell>
                  <TableCell align="center">
                    {pcp.maxPatient ?? "No limit"}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {pcp.highRisk && (
                        <Chip label="HR" size="small" color="warning" />
                      )}
                      {pcp.highCost && (
                        <Chip label="HC" size="small" color="info" />
                      )}
                      {pcp.medicare && (
                        <Chip label="MCR" size="small" color="primary" />
                      )}
                      {pcp.critical && (
                        <Chip label="CRIT" size="small" color="error" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={!!pcp.status ? "Active" : "Inactive"}
                      size="small"
                      color={!!pcp.status ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    {!!pcp.status ? (
                      <>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEdit(pcp)}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Deactivate">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              setConfirmAction({ type: "deactivate", pcp })
                            }
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <Tooltip title="Reactivate">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() =>
                            setConfirmAction({ type: "reactivate", pcp })
                          }
                        >
                          <RestoreIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* PCP Form Dialog */}
      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingPcp ? "Edit PCP Config" : "New PCP Config"}
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small" disabled={Boolean(editingPcp)}>
                <InputLabel>Location *</InputLabel>
                <Select
                  value={form.locationId}
                  label="Location *"
                  onChange={(e) =>
                    setForm((p) => ({ ...p, locationId: e.target.value }))
                  }
                >
                  {locations
                    .filter((l) => l.status === 1 && l.clinical)
                    .map((l) => (
                      <MenuItem key={l.locationId} value={l.locationId}>
                        {l.medicalCenter}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Title</InputLabel>
                <Select
                  value={form.title}
                  label="Title"
                  onChange={(e) =>
                    setForm((p) => ({ ...p, title: e.target.value }))
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  {PCP_TITLES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField
                label="Age Min"
                value={form.ageMin}
                onChange={(e) =>
                  setForm((p) => ({ ...p, ageMin: e.target.value }))
                }
                fullWidth
                size="small"
                type="number"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField
                label="Age Max"
                value={form.ageMax}
                onChange={(e) =>
                  setForm((p) => ({ ...p, ageMax: e.target.value }))
                }
                fullWidth
                size="small"
                type="number"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField
                label="Max Patients"
                value={form.maxPatient}
                onChange={(e) =>
                  setForm((p) => ({ ...p, maxPatient: e.target.value }))
                }
                fullWidth
                size="small"
                type="number"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField
                label="Days/Week"
                value={form.daysWork}
                onChange={(e) =>
                  setForm((p) => ({ ...p, daysWork: e.target.value }))
                }
                fullWidth
                size="small"
                type="number"
                slotProps={{ htmlInput: { min: 0, max: 7 } }}
              />
            </Grid>
            <Grid size={12}>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.highRisk}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, highRisk: e.target.checked }))
                      }
                    />
                  }
                  label="High Risk"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.highCost}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, highCost: e.target.checked }))
                      }
                    />
                  }
                  label="High Cost"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.medicare}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, medicare: e.target.checked }))
                      }
                    />
                  }
                  label="Medicare"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.critical}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, critical: e.target.checked }))
                      }
                    />
                  }
                  label="Critical"
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Deactivate/Reactivate */}
      <ConfirmDialog
        open={Boolean(confirmAction)}
        title={
          confirmAction?.type === "deactivate"
            ? "Deactivate PCP Config"
            : "Reactivate PCP Config"
        }
        message={
          confirmAction?.type === "deactivate"
            ? `Deactivate PCP config at ${confirmAction?.pcp?.medicalCenter}? Patients may be affected.`
            : `Reactivate PCP config at ${confirmAction?.pcp?.medicalCenter}?`
        }
        confirmLabel={
          confirmAction?.type === "deactivate" ? "Deactivate" : "Reactivate"
        }
        confirmColor={
          confirmAction?.type === "deactivate" ? "error" : "success"
        }
        onConfirm={
          confirmAction?.type === "deactivate"
            ? handleDeactivate
            : handleReactivate
        }
        onCancel={() => setConfirmAction(null)}
        loading={saving}
      />
    </Box>
  );
};

export default PcpConfigPanel;
